/**
 * Pure functions for determining available order lifecycle actions.
 *
 * @see AC-2 through AC-9 in docs/specifications/2026-04-22-admin-order-creation-ui/spec.md
 */
import type { BillingOrderDetail } from "../../../../types/adminBilling";
import { addDaysToIsoDate } from "./orderFormatters";

// =============================================================================
// HELPER: Find proforma document
// =============================================================================

/**
 * Finds the first open (ISSUED or SENT) PROFORMA document from order documents.
 * Returns undefined if none exists (payment button should be disabled).
 *
 * Mirrors the backend's definition of "has proforma" — `fetchOpenProformaOrderIds`
 * in `admin-order.service.ts` and `PendingProformaLookupService` both treat
 * ISSUED ∪ SENT as open (CANCELED is withdrawn). The backend is the source of
 * truth for document-state semantics; this must not silently diverge from it.
 *
 * @see AC-6 (2026-09-07-proforma-reczny-termin-platnosci)
 */
export const findIssuedProforma = (
  order: BillingOrderDetail
): { id: string } | undefined => {
  return order.documents?.find(
    (doc) =>
      doc.type === "PROFORMA" &&
      (doc.status === "ISSUED" || doc.status === "SENT")
  );
};

/**
 * Whether the order has available actions (sticky bar should be visible).
 */
export const hasAvailableActions = (order: BillingOrderDetail): boolean => {
  return order.status === "DRAFT" || order.status === "CONFIRMED";
};

// =============================================================================
// ACTION VISIBILITY
// =============================================================================

export interface OrderActions {
  showPriceButton: boolean;
  showProformaButton: boolean;
  showPaymentButton: boolean;
  showConfirmButton: boolean;
  showCancelButton: boolean;
}

/**
 * Derives which action buttons should be visible for the given order.
 *
 * @param order - The order detail object
 * @param isFullyPaid - Local state flag set after a successful payment registration
 */
export function getAvailableActions(
  order: BillingOrderDetail,
  isFullyPaid: boolean
): OrderActions {
  const isDraft = order.status === "DRAFT";
  const isZeroPln = order.totalGrossCents === 0;
  const issuedProforma = findIssuedProforma(order);

  return {
    // AC-6: visible when DRAFT, totalNetCents === 0, and no issued proforma
    showPriceButton: isDraft && order.totalNetCents === 0 && !issuedProforma,
    // AC-7: visible when DRAFT, totalNetCents > 0, and no issued proforma
    showProformaButton: isDraft && order.totalNetCents > 0 && !issuedProforma,
    // AC-2: visible when DRAFT and not zero PLN
    showPaymentButton: isDraft && !isZeroPln,
    // AC-8: visible when DRAFT + (zero PLN OR isFullyPaid)
    showConfirmButton: isDraft && (isZeroPln || isFullyPaid),
    // AC-4: visible when DRAFT or CONFIRMED
    showCancelButton: isDraft || order.status === "CONFIRMED",
  };
}

// =============================================================================
// PROFORMA DUE DATE (AC-4)
// =============================================================================

/**
 * Frontend prefill fallback for the proforma due-date dialog default.
 * Deliberately duplicates the backend fallback `DEFAULT_PROFORMA_DUE_DAYS`
 * (`proforma.service.ts`) — the two serve different purposes (UI prefill vs.
 * server-side default when no `dueDate` is sent) and are allowed to diverge,
 * but today they match (7 days).
 */
export const PROFORMA_DUE_DATE_PREFILL_DAYS = 7;

/** Upper bound of the allowed due-date range — mirrors the backend range policy (AC-2b). */
export const PROFORMA_DUE_DATE_MAX_DAYS = 731;

/**
 * Default due-date prefill for the "Generuj proformę" dialog (AC-4):
 * the first line's `periodStart` when it's after today, otherwise today + 7.
 */
export function getDefaultProformaDueDate(
  order: BillingOrderDetail,
  today: string
): string {
  const firstLinePeriodStartDay = order.lines?.[0]?.periodStart?.slice(0, 10);
  if (firstLinePeriodStartDay && firstLinePeriodStartDay > today) {
    return firstLinePeriodStartDay;
  }
  return addDaysToIsoDate(today, PROFORMA_DUE_DATE_PREFILL_DAYS);
}

/** `[today, today + 731 days]` — mirrors the backend range policy (AC-2b) for the native date input. */
export function getProformaDueDateRange(today: string): {
  min: string;
  max: string;
} {
  return { min: today, max: addDaysToIsoDate(today, PROFORMA_DUE_DATE_MAX_DAYS) };
}
